import { SlotGame } from "@nolimitcity/slot-game/bin/SlotGame";
import { gsap } from "gsap";
import { AnimatedSprite, Container, Sprite, Texture } from "pixi.js";

import { EventsManager } from "../../../../abstracts/events/EventsManager";
import { AbstractGameMode } from "../../../../abstracts/flow/AbstractGameMode";
import { generateScaledSprite } from "../../../../abstracts/utils/GraphicUtils";
import { playSound } from "../../../../abstracts/utils/SoundUtils";
import { Apocalypse } from "../../../Apocalypse";
import {
    ApocalypseFontsNames,
    ApocalypseSoundNames,
} from "../../../assetsLoader/ApocalypseAssetsNames";
import { UPDATE_SECOND_STAGE_FS_LEFT } from "../../../events/ApocalypseEvents";
import { ApocalypseCounter } from "../../../utils/ApocalypseCounter";

export class ApocalypseFsLeftContainer extends Container {
    private stage1SkullSprite: Sprite;
    private stage2SkullSprite: Sprite;
    private counterGlassSprite: Sprite;
    private stage1GlitchAnimSprite: AnimatedSprite;
    private stage2GlitchAnimSprite: AnimatedSprite;
    // private greenCounter: ApocalypseCounter;
    private redCounter: ApocalypseCounter;

    protected counterBackground: Sprite;

    public counter: ApocalypseCounter;

    constructor(name: string = `ApocalypseFsLeftContainer`) {
        super();
        this.name = name;

        this.initializeExtraBackgroundAndText();
        this.initializeCounterBackground();
        this.initializeCounter();

        this.handleEvents();
    }

    public switchGraphics(isFirstStage: boolean): void {
        const tl = gsap.timeline({
            onStart: () => {
                playSound({
                    name: ApocalypseSoundNames.RESET_FS_LEFT_COUNTER,
                });
                this.addChild(this.stage1GlitchAnimSprite);
                this.addChild(this.stage2GlitchAnimSprite);
            },
            onComplete: () => {
                this.removeChild(this.stage1GlitchAnimSprite);
                this.removeChild(this.stage2GlitchAnimSprite);
            },
        });

        const showFirstGlitchAnimation = gsap.fromTo(
            this.stage1GlitchAnimSprite,
            { alpha: 0 },
            {
                alpha: 1,
                duration: 0.04,
                yoyo: true,
                repeat: 1,
                repeatDelay: 0.4,
                onStart: () => {
                    gsap.delayedCall(0.04, () => {
                        this.stage1GlitchAnimSprite.gotoAndPlay(0);
                    });
                },
            }
        );

        tl.add(showFirstGlitchAnimation);

        const showSecondGlitchAnimation = gsap.fromTo(
            this.stage2GlitchAnimSprite,
            { alpha: 0 },
            {
                alpha: 1,
                duration: 0.04,
                yoyo: true,
                repeat: 1,
                repeatDelay: 0.4,
                onStart: () => {
                    gsap.delayedCall(0.04, () => {
                        this.stage2GlitchAnimSprite.gotoAndPlay(0);
                    });
                },
            }
        );

        tl.add(showSecondGlitchAnimation, 0.17);

        this.stage1SkullSprite.texture = Texture.from(
            isFirstStage ? `apocalypse/fsCounterskull_1` : `apocalypse/fsCounterskull_2`
        );

        this.stage2SkullSprite.texture = Texture.from(
            isFirstStage ? `apocalypse/fsCounterskull_4` : `apocalypse/fsCounterskull_3`
        );

        this.counter.y = isFirstStage ? -54 : 21;
        this.redCounter.y = isFirstStage ? 21 : -54;
        this.redCounter.getUpdateCounterTextAnimation(0, true);
    }

    protected handleEvents(): void {
        EventsManager.on(UPDATE_SECOND_STAGE_FS_LEFT, () => {
            if (Apocalypse.model.data.firstStage) {
                let secondStageFsLeft = 0;
                switch (Apocalypse.model.data.nextMode) {
                    case AbstractGameMode.SUPER_FREESPIN:
                    case AbstractGameMode.FREESPIN_BETTER:
                        secondStageFsLeft = 10;
                        break;

                    default:
                        secondStageFsLeft = 5;
                        break;
                }

                this.redCounter.getUpdateCounterTextAnimation(secondStageFsLeft, true);
            } else if (Apocalypse.model.data.secondStage) {
                this.redCounter.getUpdateCounterTextAnimation(0, true);
            } else {
                this.redCounter.getUpdateCounterTextAnimation(0, true);
            }
        });
    }

    protected initializeExtraBackgroundAndText(): void {
        this.stage1SkullSprite = generateScaledSprite(`apocalypse/fsCounterskull_1`);
        this.stage1SkullSprite.name = `redSkull`;
        this.stage1SkullSprite.anchor.set(0.5);

        this.stage1SkullSprite.x = -80;
        this.stage1SkullSprite.y = -50;

        this.addChild(this.stage1SkullSprite);

        this.stage2SkullSprite = generateScaledSprite(`apocalypse/fsCounterskull_4`);
        this.stage2SkullSprite.name = `greenSkull`;
        this.stage2SkullSprite.anchor.set(0.5);

        this.stage2SkullSprite.x = this.stage1SkullSprite.x;
        this.stage2SkullSprite.y = 25;

        this.addChild(this.stage2SkullSprite);
    }

    protected initializeCounterBackground(): void {
        this.counterBackground = this.getFsBackground();
        this.addChildAt(this.counterBackground, 0);
    }

    protected initializeCounter(): void {
        this.redCounter = new ApocalypseCounter({
            initialValue: 0,
            fontProps: {
                text: ``,
                style: {
                    fontName: ApocalypseFontsNames.APOCALYPSE_RED,
                    fontSize: 9,
                },
            },
            highlightColor: `yellow`,
            name: `redCounter`,
        });

        this.redCounter.x = -30;
        this.redCounter.y = 21;

        this.addChild(this.redCounter);

        this.counter = new ApocalypseCounter({
            initialValue: 3,
            fontProps: {
                text: ``,
                style: {
                    fontName: ApocalypseFontsNames.APOCALYPSE_GREEN,
                    fontSize: 9,
                },
            },
            highlightColor: `white`,
            name: `fsLeftCounter`,
            onCounterStart: () => {
                if (
                    Apocalypse.model.gameData &&
                    this.counter.lastValue < this.counter.valueObj.value
                ) {
                    playSound({
                        name: ApocalypseSoundNames.FS_SPIN_WON_COUNTDOWN,
                    });
                }
            },
            onCounterComplete: () => {
                if (!Apocalypse.model.data.isRestoreState) {
                    SlotGame.keypadPlugin.setZeroBetSpinCounter(this.counter.valueObj.value);
                }
            },
        });

        this.counter.x = this.redCounter.x;
        this.counter.y = -54;

        this.addChild(this.counter);

        let glitchTextures = [];

        for (let i = 0; i < 18; i++) {
            const textureName = `Apocalypse/indicator_transition/indicator_transition_000${
                i < 10 ? `0${i}` : i
            }`;

            glitchTextures.push(Texture.from(textureName));
        }

        this.stage1GlitchAnimSprite = new AnimatedSprite(glitchTextures);
        this.stage1GlitchAnimSprite.scale.set(0.5);
        this.stage1GlitchAnimSprite.anchor.set(0.5);
        this.stage1GlitchAnimSprite.width = 100;
        this.stage1GlitchAnimSprite.height = 62;
        this.stage1GlitchAnimSprite.x = -55;
        this.stage1GlitchAnimSprite.y = -50;
        this.stage1GlitchAnimSprite.name = `stage1Glitch`;
        this.stage1GlitchAnimSprite.loop = false;

        this.stage2GlitchAnimSprite = new AnimatedSprite(glitchTextures);
        this.stage2GlitchAnimSprite.scale.set(0.5);
        this.stage2GlitchAnimSprite.anchor.set(0.5);
        this.stage2GlitchAnimSprite.width = this.stage1GlitchAnimSprite.width;
        this.stage2GlitchAnimSprite.height = this.stage1GlitchAnimSprite.height;
        this.stage2GlitchAnimSprite.x = this.stage1GlitchAnimSprite.x;
        this.stage2GlitchAnimSprite.y = 25;
        this.stage2GlitchAnimSprite.name = `stage2Glitch`;
        this.stage2GlitchAnimSprite.loop = false;

        this.counterGlassSprite = generateScaledSprite(`apocalypse/fsCounterGlass`);
        this.counterGlassSprite.x = -106;
        this.counterGlassSprite.y = -81;
        this.addChild(this.counterGlassSprite);
    }

    protected getFsBackground(): Sprite {
        const fsBackground = generateScaledSprite(`apocalypse/fsCounterBackground`);
        fsBackground.name = `fsLeftCounterBg`;
        fsBackground.x = -116; //! check mobile
        fsBackground.y = -100;

        return fsBackground;
    }

    public reset(): void {
        // @ts-ignore
        this.filters = null;

        this.alpha = 1;

        this.counter.updateValue(`3`);
    }
}
